import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  MenuItem,
  Typography,
  Stack,
  CircularProgress,
  Fade,
  Chip,
  IconButton,
  Alert,
  LinearProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { messages } from "../../../../locales/messages";
import { adminApiService } from "../../../../utils/api";
import {
  CreateGfFigmaTemplateDataDto,
  UpdateGfFigmaTemplateDataDto,
  GfFlyerItemCategory,
  GfStore,
  UpdateGfFigmaTemplateDataDto as UpdateDto,
  BulkGenerationStatusDto,
} from "@swagger/typescript-fetch-goflyer";
import { makeStoreDisplayName } from "../../../../utils/dtoUtil/makeStoreDisplayName";
import { useFlyerItemCategories } from "../../../../hooks";
import { useStores } from "../../../../hooks/useStores";
import { useFigmaTemplateTagOptions } from "../../../../hooks/useFigmaTemplateTagOptions";
import { toastError, toastSuccess } from "../../../../utils/pageUtil/toast";
import { PermissionComponentWrapper } from "../../../components/PermissionComponentWrapper";
import SaveIcon from "@mui/icons-material/Save";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CategoryIcon from "@mui/icons-material/Category";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseIcon from "@mui/icons-material/Close";
import AssessmentIcon from "@mui/icons-material/Assessment";
import { CustomAutocompleteMultiSelect } from "../../../components/CustomAutocomplete";
import {
  DEFAULT_FIGMA_TEMPLATE_FORM,
  FigmaTemplateFormState,
  FigmaTemplateFormErrors,
  validateFigmaTemplateForm,
  isFormValid,
  buildCreateOrUpdatePayload,
} from "./figmaTemplateDataFormUtils";
import {
  ModernContainer,
  HeaderCard,
  SectionCard,
  SectionHeader,
  SectionIcon,
  StyledTextField,
  StyledIconButton,
  PrimaryButton,
  SecondaryButton,
  ActionBar,
  LoadingOverlay,
  FormGrid,
  StatusChip,
  AutoCompleteWrapper,
  ElevatedSectionCard,
  PRIMARY_COLOR,
  NEUTRAL_800,
  NEUTRAL_900,
} from "./styles";
import FigmaFileUploadSection from "../../../../components/FigmaFileUploadSection";
import PreviewImageSection from "../../../components/PreviewImageSection/PreviewImageSection";
import { useAuthHeaders } from "../../../../hooks/useAuthHeaders";
import { AutomatedLayoutTestModal } from "./AutomatedLayoutTestModal";

type TemplateType = CreateGfFigmaTemplateDataDto.TypeEnum | UpdateDto.TypeEnum;
type FormState = FigmaTemplateFormState;
const DEFAULT_FORM = DEFAULT_FIGMA_TEMPLATE_FORM;

const FigmaTemplateDataCreateEdit: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { figmaTemplateId } = useParams<{ figmaTemplateId?: string }>();
  const authHeaders = useAuthHeaders();
  const isEdit = Boolean(figmaTemplateId);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [errors, setErrors] = useState<FigmaTemplateFormErrors>({});
  const [touchedFields, setTouchedFields] = useState<Set<keyof FormState>>(
    new Set()
  );
  const [showErrorAlert, setShowErrorAlert] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [isFigmaFileUploading, setIsFigmaFileUploading] = useState(false);
  const [showTestConfirmDialog, setShowTestConfirmDialog] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [bulkStatusData, setBulkStatusData] =
    useState<BulkGenerationStatusDto | null>(null);
  const [loadingBulkStatus, setLoadingBulkStatus] = useState(false);

  const { flyerItemCategories } = useFlyerItemCategories();
  const { storesList, isStoreOptionLoading, handleOnStoreSearch } = useStores();
  const { figmaTemplateTagOptionList } = useFigmaTemplateTagOptions();

  const typeOptions: { label: string; value: TemplateType }[] = useMemo(
    () => [
      { label: "Public", value: CreateGfFigmaTemplateDataDto.TypeEnum.Public },
      {
        label: "Specific",
        value: CreateGfFigmaTemplateDataDto.TypeEnum.Specific,
      },
    ],
    []
  );

  const loadData = useCallback(async () => {
    if (!figmaTemplateId) return;
    setLoading(true);
    try {
      const data =
        await adminApiService.getOneBaseGfFigmaTemplateDataAdminControllerGfFigmaTemplateData(
          figmaTemplateId,
          undefined,
          [
            "figmaTemplateTag",
            "category",
            "stores",
            "image",
            "figmaTemplateFiles",
          ],
          undefined
        );
      setForm({
        name: data.name || "",
        figmaTemplateTag: data.figmaTemplateTag,
        image: data.image,
        numberOfPages: data.numberOfPages ?? "",
        category: data.category || [],
        type: data.type,
        stores: data.stores || [],
        figmaTemplateFiles: data.figmaTemplateFiles || [],
        isDeleted: data.isDeleted ?? false,
      });
    } catch (e) {
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setLoading(false);
    }
  }, [figmaTemplateId, t]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const fetchBulkGenerationStatus = async () => {
    if (!figmaTemplateId) return;
    setLoadingBulkStatus(true);
    try {
      const data =
        await adminApiService.gfFlyerItemFigmaJobAdminControllerGetBulkGenerationStatus(
          figmaTemplateId,
          authHeaders
        );
      setBulkStatusData(data);
      setShowBulkStatusModal(true);
    } catch (e) {
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setLoadingBulkStatus(false);
    }
  };

  const updateField = <K extends keyof FormState>(
    key: K,
    value: FormState[K]
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouchedFields((prev) => new Set(prev).add(key));
  };

  useEffect(() => {
    const allErrors = validateFigmaTemplateForm(form, t, messages);
    const touchedErrors: FigmaTemplateFormErrors = {};
    Object.keys(allErrors).forEach((key) => {
      const fieldKey = key as keyof FigmaTemplateFormErrors;
      if (touchedFields.has(fieldKey as keyof FormState)) {
        touchedErrors[fieldKey] = allErrors[fieldKey];
      }
    });
    setErrors(touchedErrors);
  }, [form, t, touchedFields]);

  const hasAnyValidationErrors =
    Object.keys(validateFigmaTemplateForm(form, t, messages)).length > 0;

  const getFormProgress = () => {
    const requiredFields = ["name", "numberOfPages", "type"];
    const completedFields = requiredFields.filter((field) => {
      switch (field) {
        case "name":
          return form.name.trim() !== "";
        case "numberOfPages":
          return form.numberOfPages !== "";
        case "type":
          return form.type !== "";
        default:
          return false;
      }
    });

    if (form.type === CreateGfFigmaTemplateDataDto.TypeEnum.Specific) {
      requiredFields.push("stores");
      if (form.stores.length > 0) {
        completedFields.push("stores");
      }
    }

    return {
      completed: completedFields.length,
      total: requiredFields.length,
      percentage: (completedFields.length / requiredFields.length) * 100,
    };
  };

  const formProgress = getFormProgress();

  const handleSave = async () => {
    const validation = validateFigmaTemplateForm(form, t, messages);

    setTouchedFields(new Set(Object.keys(DEFAULT_FORM) as (keyof FormState)[]));
    setErrors(validation);

    if (!isFormValid(validation)) {
      setShowErrorAlert(true);

      const firstErrorField = Object.keys(validation)[0];
      if (firstErrorField) {
        setTimeout(() => {
          const element = document.querySelector(
            `[name="${firstErrorField}"], [data-testid="${firstErrorField}-select"]`
          );
          if (element) {
            element.scrollIntoView({ behavior: "smooth", block: "center" });
          }
        }, 100);

        const firstError = Object.values(validation)[0];
        if (firstError) toastError(firstError);
      }
      return;
    }
    setSaving(true);
    try {
      const payload = buildCreateOrUpdatePayload(form);
      if (isEdit && figmaTemplateId) {
        await adminApiService.updateOneBaseGfFigmaTemplateDataAdminControllerGfFigmaTemplateData(
          payload as UpdateGfFigmaTemplateDataDto,
          figmaTemplateId,
          authHeaders
        );
        toastSuccess(t(messages.Update()));
      } else {
        await adminApiService.createOneBaseGfFigmaTemplateDataAdminControllerGfFigmaTemplateData(
          payload as CreateGfFigmaTemplateDataDto,
          authHeaders
        );
        toastSuccess(t(messages.Create()));
      }
      navigate(-1);
    } catch (e) {
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    } finally {
      setSaving(false);
    }
  };

  const hasErrors = Object.keys(errors).length > 0;

  const handleTestLayoutClick = () => {
    setShowTestConfirmDialog(true);
  };

  const handleTestLayoutConfirm = async () => {
    setShowTestConfirmDialog(false);
    try {
      if (figmaTemplateId) {
        const response =
          await adminApiService.gfFlyerItemFigmaJobAdminControllerTriggerBulkGeneration(
            {
              templateId: figmaTemplateId,
            },
            authHeaders
          );

        if (response.success) {
          toastSuccess(
            response.message || t(messages.Template_Layout_Test_Triggered())
          );
        } else {
          toastError(
            response.message ||
              t(messages.There_is_an_error_please_Contact_Administrator())
          );
        }
      }
    } catch (e) {
      const errorMessage =
        e instanceof Error
          ? e.message
          : t(messages.There_is_an_error_please_Contact_Administrator());
      toastError(errorMessage);
    }
  };

  const handleTestLayoutCancel = () => {
    setShowTestConfirmDialog(false);
  };

  return (
    <ModernContainer
      maxWidth="lg"
      data-testid="figma-template-create-edit-container"
    >
      <Fade in timeout={600}>
        <div data-testid="figma-template-create-edit-content">
          <HeaderCard data-testid="figma-template-create-edit-header">
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
              <StyledIconButton
                onClick={() => navigate(-1)}
                data-testid="figma-template-create-edit-back-button"
              >
                <ArrowBackIcon sx={{ color: PRIMARY_COLOR }} />
              </StyledIconButton>
              <Box flex={1}>
                <Typography
                  variant="h4"
                  fontWeight="700"
                  color={NEUTRAL_900}
                  mb={1}
                >
                  {isEdit
                    ? t(messages.Edit_Figma_Template())
                    : t(messages.Create_Figma_Template())}
                </Typography>
                <Typography variant="body1" color="text.secondary">
                  {isEdit
                    ? t(messages.Update_Your_Figma_Template_Configuration())
                    : t(
                        messages.Set_Up_A_New_Figma_Template_For_Your_Designs()
                      )}
                </Typography>
              </Box>
              <StatusChip
                label={isEdit ? t(messages.Editing()) : t(messages.New())}
                color={isEdit ? "info" : "success"}
                variant="filled"
                data-testid="figma-template-create-edit-status-chip"
              />
            </Stack>

            {/* Required Fields Progress Indicator - Shows completion status of mandatory form fields */}
            <Box sx={{ mt: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2} mb={1}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontSize="0.875rem"
                >
                  {t(messages.Required_Field())}: {formProgress.completed}/
                  {formProgress.total}
                </Typography>
                <Box flex={1}>
                  <LinearProgress
                    variant="determinate"
                    value={formProgress.percentage}
                    sx={{
                      height: 6,
                      borderRadius: 3,
                      backgroundColor: `${PRIMARY_COLOR}20`,
                      "& .MuiLinearProgress-bar": {
                        borderRadius: 3,
                        backgroundColor: PRIMARY_COLOR,
                      },
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color={PRIMARY_COLOR}
                  fontSize="0.875rem"
                >
                  {Math.round(formProgress.percentage)}%
                </Typography>
              </Stack>
            </Box>

            {showErrorAlert && hasAnyValidationErrors && (
              <Alert
                severity="error"
                sx={{ borderRadius: "12px", mb: 2 }}
                action={
                  <IconButton
                    size="small"
                    onClick={() => setShowErrorAlert(false)}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                }
                data-testid="figma-template-create-edit-validation-alert"
              >
                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight="600"
                    mb={1}
                    data-testid="figma-template-create-edit-error-title"
                  >
                    {t(messages.Please_Fix_Following_Issues())}
                  </Typography>
                  {Object.entries(
                    validateFigmaTemplateForm(form, t, messages)
                  ).map(([field, error]) => (
                    <Typography
                      key={field}
                      variant="body2"
                      component="div"
                      sx={{ mb: 0.5 }}
                      data-testid={`figma-template-create-edit-error-${field}`}
                    >
                      • {error}
                    </Typography>
                  ))}
                </Box>
              </Alert>
            )}
          </HeaderCard>

          {loading ? (
            <SectionCard data-testid="figma-template-create-edit-loading">
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                py={8}
                data-testid="figma-template-create-edit-loading-container"
              >
                <Stack alignItems="center" spacing={2}>
                  <CircularProgress size={48} sx={{ color: PRIMARY_COLOR }} />
                  <Typography variant="body1" color="text.secondary">
                    {t(messages.Loading_Template_Data())}
                  </Typography>
                </Stack>
              </Box>
            </SectionCard>
          ) : (
            <Box
              component="form"
              noValidate
              sx={{ overflow: "visible" }}
              onSubmit={(e) => {
                e.preventDefault();
                handleSave();
              }}
              data-testid="figma-template-create-edit-form"
            >
              <SectionCard data-testid="figma-template-create-edit-basic-info">
                {/* Basic Information section */}
                <SectionHeader>
                  <SectionIcon>
                    <InfoOutlinedIcon />
                  </SectionIcon>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      color={NEUTRAL_900}
                    >
                      {t(messages.Basic_Information())}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(messages.Essential_Details_About_Your_Template())}
                    </Typography>
                  </Box>
                </SectionHeader>

                <Stack spacing={3}>
                  {/* Template Name field */}
                  <StyledTextField
                    name="name"
                    type="text"
                    label={t(messages.Template_Name())}
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    fullWidth
                    required
                    error={!!errors.name}
                    helperText={errors.name}
                    placeholder={t(
                      messages.Enter_A_Descriptive_Name_For_Your_Template()
                    )}
                    data-testid="figma-template-create-edit-name-input"
                  />

                  <FormGrid>
                    {/* Template Tag field */}
                    <StyledTextField
                      select
                      label={t(messages.Template_Tag())}
                      value={form.figmaTemplateTag?.id || ""}
                      onChange={(e) => {
                        const tag = figmaTemplateTagOptionList?.find(
                          (tg) => tg.id === e.target.value
                        );
                        updateField("figmaTemplateTag", tag);
                      }}
                      fullWidth
                      data-testid="figma-template-create-edit-tag-select"
                    >
                      <MenuItem value="">
                        <em>{t(messages.No_Tag_Selected())}</em>
                      </MenuItem>
                      {figmaTemplateTagOptionList?.map((tag) => (
                        <MenuItem key={tag.id} value={tag.id}>
                          {tag.name}
                        </MenuItem>
                      ))}
                    </StyledTextField>

                    {/* Number of Pages field */}
                    <StyledTextField
                      name="numberOfPages"
                      type="number"
                      label={t(messages.Number_Of_Pages())}
                      value={form.numberOfPages}
                      onChange={(e) =>
                        updateField(
                          "numberOfPages",
                          e.target.value === "" ? "" : Number(e.target.value)
                        )
                      }
                      fullWidth
                      required
                      inputProps={{ min: 1, max: 100 }}
                      error={!!errors.numberOfPages}
                      helperText={
                        errors.numberOfPages ||
                        t(messages.How_Many_Pages_Does_This_Template_Contain())
                      }
                      placeholder={t(messages.E_G_2())}
                      data-testid="figma-template-create-edit-pages-input"
                    />
                  </FormGrid>
                </Stack>
              </SectionCard>

              <PreviewImageSection
                image={form.image}
                onImageChange={(image) => updateField("image", image)}
                error={errors.image}
                onUploadingChange={setIsImageUploading}
              />

              <ElevatedSectionCard>
                {/* Categories section */}
                <SectionHeader>
                  <SectionIcon>
                    <CategoryIcon />
                  </SectionIcon>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      color={NEUTRAL_900}
                    >
                      {t(messages.Categories())}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(
                        messages.Classify_Your_Template_By_Relevant_Categories()
                      )}
                    </Typography>
                  </Box>
                  <Chip
                    label={t(messages.Optional())}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ ml: "auto" }}
                  />
                </SectionHeader>

                <Box>
                  <AutoCompleteWrapper>
                    <CustomAutocompleteMultiSelect
                      title=""
                      toolTipInfoText="Select one or more categories"
                      optionsList={flyerItemCategories.map((category) => ({
                        value: category,
                        label: category.name,
                      }))}
                      selectedValues={form.category.map((category) => ({
                        value: category,
                        label: category.name,
                      }))}
                      multiSelect
                      handleOnChange={(
                        value: GfFlyerItemCategory[] | undefined
                      ) => updateField("category", value || [])}
                      WrapperCss="width: 100%; position: relative; z-index: 2000;"
                      inputCss="width: 100%; border-radius: 12px;"
                      selectListCss="z-index: 2100; position: absolute; max-width: none; width: 100%;"
                      dataTestId="figma-template-create-edit-category-select"
                    />
                  </AutoCompleteWrapper>
                  {errors.category && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 1, display: "block" }}
                    >
                      {errors.category}
                    </Typography>
                  )}
                </Box>
              </ElevatedSectionCard>

              <ElevatedSectionCard>
                <SectionHeader>
                  <SectionIcon>
                    <VisibilityIcon />
                  </SectionIcon>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      color={NEUTRAL_900}
                    >
                      {t(messages.Template_Visibility())}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(messages.Define_Who_Can_Access_This_Template())}
                    </Typography>
                  </Box>
                </SectionHeader>

                <Stack spacing={3}>
                  <StyledTextField
                    name="type"
                    select
                    label={t(messages.Visibility_Type())}
                    value={form.type}
                    onChange={(e) =>
                      updateField(
                        "type",
                        e.target.value as unknown as TemplateType
                      )
                    }
                    fullWidth
                    required
                    error={!!errors.type}
                    helperText={
                      errors.type ||
                      t(
                        messages.Choose_Whether_This_Template_Is_Public_Or_Store_Specific()
                      )
                    }
                    data-testid="figma-template-create-edit-visibility-select"
                  >
                    <MenuItem value="" disabled>
                      <em>{t(messages.Select_Visibility_Type())}</em>
                    </MenuItem>
                    {typeOptions.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {opt.value ===
                          CreateGfFigmaTemplateDataDto.TypeEnum.Public ? (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "#4CAF50",
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 8,
                                height: 8,
                                borderRadius: "50%",
                                backgroundColor: "#FF9800",
                              }}
                            />
                          )}
                          <span>{opt.label}</span>
                        </Stack>
                      </MenuItem>
                    ))}
                  </StyledTextField>

                  {form.type ===
                    CreateGfFigmaTemplateDataDto.TypeEnum.Specific && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight="600"
                        mb={2}
                        color={NEUTRAL_800}
                      >
                        {t(messages.Select_Specific_Stores())}
                      </Typography>
                      <AutoCompleteWrapper>
                        <CustomAutocompleteMultiSelect
                          title=""
                          optionsList={(storesList || []).map((store) => ({
                            value: store,
                            label: makeStoreDisplayName(store),
                          }))}
                          isLoading={isStoreOptionLoading}
                          selectedValues={form.stores.map((store) => ({
                            value: store,
                            label: makeStoreDisplayName(store),
                          }))}
                          multiSelect
                          handleOnChange={(stores: GfStore[] | undefined) =>
                            updateField("stores", stores || [])
                          }
                          handleOnSearch={handleOnStoreSearch}
                          WrapperCss="width: 100%; position: relative; z-index: 2000;"
                          inputCss="width: 100%; border-radius: 12px;"
                          selectListCss="z-index: 2100; position: absolute; max-width: none; width: 100%;"
                          dataTestId="figma-template-create-edit-stores-select"
                        />
                      </AutoCompleteWrapper>
                      {errors.stores && (
                        <Typography
                          variant="caption"
                          color="error"
                          sx={{ mt: 1, display: "block" }}
                        >
                          {errors.stores}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Stack>
              </ElevatedSectionCard>

              <SectionCard>
                <SectionHeader>
                  <SectionIcon>
                    <DescriptionIcon />
                  </SectionIcon>
                  <Box>
                    <Typography
                      variant="h6"
                      fontWeight="600"
                      color={NEUTRAL_900}
                    >
                      {t(messages.Figma_File())}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t(messages.Upload_Or_Link_To_Your_Figma_Template_File())}
                    </Typography>
                  </Box>
                  <Chip
                    label={t(messages.Optional())}
                    size="small"
                    color="info"
                    variant="outlined"
                    sx={{ ml: "auto" }}
                  />
                </SectionHeader>

                <Stack spacing={3}>
                  <FigmaFileUploadSection
                    value={form.figmaTemplateFiles?.[0]}
                    onChange={(file) =>
                      updateField("figmaTemplateFiles", file ? [file] : [])
                    }
                    onUploadingChange={setIsFigmaFileUploading}
                  />
                </Stack>
              </SectionCard>

              <ActionBar>
                <Stack
                  direction={{ xs: "column", sm: "column", md: "row" }}
                  justifyContent={{ xs: "stretch", md: "space-between" }}
                  alignItems={{ xs: "stretch", md: "center" }}
                  spacing={2}
                  sx={{
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: { xs: "none", md: "block" },
                      flex: { md: 1 },
                    }}
                  >
                    {showErrorAlert && hasAnyValidationErrors && (
                      <Box data-testid="figma-template-create-edit-action-errors">
                        <Typography
                          variant="subtitle2"
                          fontWeight="600"
                          mb={1}
                          color="error"
                        >
                          {t(messages.Please_Fix_Following_Issues())}
                        </Typography>
                        {Object.entries(
                          validateFigmaTemplateForm(form, t, messages)
                        ).map(([field, error]) => (
                          <Typography
                            key={field}
                            variant="body2"
                            component="div"
                            sx={{ mb: 0.5 }}
                            color="error"
                            data-testid={`figma-template-create-edit-action-error-${field}`}
                          >
                            • {error}
                          </Typography>
                        ))}
                      </Box>
                    )}

                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ color: PRIMARY_COLOR, fontWeight: "bold" }}
                    >
                      {!hasErrors && t(messages.Ready_To_Save())}
                    </Typography>
                  </Box>
                  <Stack
                    direction={{ xs: "column", sm: "column", md: "row" }}
                    alignItems="stretch"
                    spacing={{ xs: 1.5, md: 2 }}
                    sx={{
                      width: { xs: "100%", md: "auto" },
                      flexWrap: { md: "wrap" },
                    }}
                    data-testid="figma-template-create-edit-action-buttons"
                  >
                    {isEdit && (
                      <>
                        <SecondaryButton
                          onClick={fetchBulkGenerationStatus}
                          disabled={saving || loadingBulkStatus}
                          startIcon={
                            loadingBulkStatus ? (
                              <CircularProgress size={18} color="inherit" />
                            ) : (
                              <AssessmentIcon />
                            )
                          }
                          data-testid="figma-template-check-status-button"
                        >
                          {loadingBulkStatus
                            ? t(messages.Loading())
                            : t(messages.Check_Status())}
                        </SecondaryButton>
                        <SecondaryButton
                          onClick={handleTestLayoutClick}
                          disabled={saving}
                          startIcon={<PlayArrowIcon />}
                          data-testid="figma-template-test-layout-button"
                        >
                          {t(messages.Test_Layout())}
                        </SecondaryButton>
                      </>
                    )}
                    <SecondaryButton
                      onClick={() => navigate(-1)}
                      disabled={saving}
                      startIcon={<ArrowBackIcon />}
                      data-testid="figma-template-create-edit-cancel-button"
                    >
                      {t(messages.Cancel())}
                    </SecondaryButton>
                    <PermissionComponentWrapper
                      requiredPermission={[isEdit ? "edit" : "create"]}
                      requiredPermissionGroup={["merchant"]}
                    >
                      <PrimaryButton
                        onClick={handleSave}
                        disabled={
                          saving || isImageUploading || isFigmaFileUploading
                        }
                        startIcon={
                          saving ? (
                            <CircularProgress size={18} color="inherit" />
                          ) : (
                            <SaveIcon />
                          )
                        }
                        data-testid="figma-template-create-edit-submit-button"
                      >
                        {saving
                          ? t(messages.Saving())
                          : isEdit
                          ? t(messages.Update())
                          : t(messages.Create())}
                      </PrimaryButton>
                    </PermissionComponentWrapper>
                  </Stack>
                </Stack>
              </ActionBar>
            </Box>
          )}

          {saving && (
            <LoadingOverlay data-testid="figma-template-create-edit-saving-overlay">
              <Stack
                alignItems="center"
                spacing={2}
                data-testid="figma-template-create-edit-saving-stack"
              >
                <CircularProgress
                  size={40}
                  sx={{ color: PRIMARY_COLOR }}
                  data-testid="figma-template-create-edit-saving-spinner"
                />
                <Typography
                  variant="body1"
                  fontWeight="500"
                  color={NEUTRAL_800}
                  data-testid="figma-template-create-edit-saving-text"
                >
                  {isEdit
                    ? t(messages.Updating_Template())
                    : t(messages.Creating_Template())}
                </Typography>
              </Stack>
            </LoadingOverlay>
          )}
        </div>
      </Fade>

      <Dialog
        open={showTestConfirmDialog}
        onClose={handleTestLayoutCancel}
        data-testid="figma-template-test-layout-dialog"
      >
        <DialogTitle>{t(messages.Confirm_Template_Layout_Test())}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t(
              messages.Are_You_Sure_You_Want_To_Trigger_Template_Layout_Test()
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleTestLayoutCancel}
            color="inherit"
            data-testid="figma-template-test-layout-cancel"
          >
            {t(messages.Cancel())}
          </Button>
          <Button
            onClick={handleTestLayoutConfirm}
            variant="contained"
            autoFocus
            data-testid="figma-template-test-layout-confirm"
            sx={{
              backgroundColor: "#f58220",
              "&:hover": { backgroundColor: "#d97020" },
            }}
          >
            {t(messages.Confirm())}
          </Button>
        </DialogActions>
      </Dialog>

      <AutomatedLayoutTestModal
        open={showBulkStatusModal}
        onClose={() => setShowBulkStatusModal(false)}
        data={bulkStatusData}
      />
    </ModernContainer>
  );
};

export default FigmaTemplateDataCreateEdit;
